import { useEffect, useState } from 'react';
import queryString from 'query-string';

import {
  ButtonDialog,
  MochiCheckbox,
  MochiInput,
  MochiRadioGroup,
} from '@autopass-web/mochi-component';

import { ActionButton, Block, PageLayout } from '../components/UI';
import { analyticsService } from '../services/amplitude';
import { useLicensePlateCreate } from '../services/use-license-plate-create';
import { useLicensePlates } from '../services/use-license-plates';
import { useUserMeUpdate } from '../services/use-user-me-update';
import { useToast } from '../store/toast';
import { userEditStore, UserInfo } from '../store/user-store';
import styles from './styles.module.sass';

const vehicleTypeOptions = [
  { label: '汽車', value: 'car' },
  { label: '機車', value: 'moto' },
] as const satisfies { value: string; label: string }[];

const VEHICLE_TYPE_KEY = 'type';
const PLATE_NUMBER = 'plate';

export function LicensePlatePage() {
  const licensePlatesQuery = useLicensePlates({ enabled: false });
  const toast = useToast();

  const [vehicleType, setVehicleType] = useState<UserInfo['vehicleType']>(
    () => {
      const { [VEHICLE_TYPE_KEY]: vehicleType } = queryString.parse(
        window.location.search,
      );

      if (vehicleType === vehicleTypeOptions[0].value) {
        return vehicleTypeOptions[0].value;
      }

      if (vehicleType === vehicleTypeOptions[1].value) {
        return vehicleTypeOptions[1].value;
      }

      return 'car';
    },
  );
  const [plateNumber, setPlateNumber] = useState(() => {
    const { [PLATE_NUMBER]: plateNumber } = queryString.parse(
      window.location.search,
    );

    if (typeof plateNumber === 'string') {
      return plateNumber;
    }

    return '';
  });

  const [showDuplicateBindingDialog, setShowDuplicateBindingDialog] =
    useState(false);
  const [enableStreetParking, setEnableStreetParking] = useState(true);

  const handleConfigComplete = () => {
    licensePlatesQuery.refetch();
    userEditStore.edit();
  };

  const { updateUserMeMutate } = useUserMeUpdate({
    onSuccess: handleConfigComplete,
    onError: () => {
      toast.onToastError('網路連線發生錯誤，請稍後再試。');
    },
  });

  const { mutate: createLicensePlate, status } = useLicensePlateCreate();

  const handleCreateSuccess = () => {
    if (enableStreetParking) {
      updateUserMeMutate({ enableOnStreetParkingAutoPay: true });
      return;
    }

    handleConfigComplete();
  };

  const handleCreate = () => {
    createLicensePlate(
      { plateNumber, vehicleType },
      {
        onSuccess: handleCreateSuccess,
        onError(error) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const e = error as any;
          const status = e.response.status;
          if (status === 422) {
            const code = e.response.data.error.code;
            if (code === 'duplicate_plate_number') {
              handleCreateSuccess();
            } else if (code === 'plate_number_already_exists') {
              setShowDuplicateBindingDialog(true);
            } else if (code === 'plate_numbers_already_max') {
              toast.onToastError('車牌數量已達上限');
            } else {
              toast.onToastError(e.response.data.error.message);
            }
          } else {
            toast.onToastError('網路連線發生錯誤，請稍後再試。');
          }
        },
      },
    );
  };

  useEffect(() => {
    analyticsService.licenseInputPageview();
  }, []);

  const actions = (
    <>
      <div className="mb-5 mt-8">
        <MochiCheckbox
          value="enableStreetParking"
          checked={enableStreetParking}
          onChange={() => {
            setEnableStreetParking((prev) => !prev);
          }}
        >
          開啟路邊停車自動扣
        </MochiCheckbox>
      </div>
      <ActionButton
        isLoading={status === 'pending'}
        onClick={() => {
          analyticsService.licenseNextStepClick();
          handleCreate();
        }}
      >
        下一步
      </ActionButton>
    </>
  );

  const options = [
    { label: '汽車', value: 'car' },
    { label: '機車', value: 'moto' },
  ] as const satisfies { label: string; value: string }[];

  return (
    <>
      <PageLayout title="麻吉麻吉，請問您的車牌號碼" bottom={actions}>
        <Block title="車牌設定" className={'flex flex-col gap-3'}>
          <MochiInput
            value={plateNumber}
            onFocus={analyticsService.licenseInputFieldClick}
            onChange={(e) => setPlateNumber(e.target.value.toUpperCase())}
            placeholder="輸入車牌號碼（含 - 橫線）"
            errorMessage={null}
          />
          <MochiRadioGroup
            className={styles['radioGroups']}
            options={options}
            value={vehicleType}
            spacing={10}
            onChange={(value) => {
              if (value === 'car') {
                analyticsService.licenseVehicleTypeCarClick();
              }

              if (value === 'moto') {
                analyticsService.licenseVehicleTypeMotorcycleClick();
              }

              if (value === 'car' || value === 'moto') {
                setVehicleType(value);
              } else {
                throw new Error('Wrong value');
              }
            }}
          />
        </Block>
      </PageLayout>
      <ButtonDialog
        title="車牌重複綁定"
        message={`${plateNumber} 已被其他手機設定，請請改設定其他車牌`}
        buttonList={[
          {
            theme: 'positive',
            content: '確認',
            onClick: () => {
              analyticsService.licenseDuplicatedDialogConfirmClick();
              setShowDuplicateBindingDialog(false);
            },
          },
        ]}
        show={showDuplicateBindingDialog}
      />
    </>
  );
}
